import { useCallback, useEffect, useMemo, useState } from "react";
import { BookUser, Boxes, Pencil, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import type { Contact, Lead } from "../leads/types";

export const contactRoleLabels: Record<string, string> = {
  seller: "卖方",
  buyer: "买方",
  both: "双边",
  "": "未标",
};

const roleBadgeClass: Record<string, string> = {
  seller: "bg-emerald-400/10 text-emerald-300",
  buyer: "bg-blue-400/10 text-blue-300",
  both: "bg-violet-400/10 text-violet-300",
};

type RoleFilter = "all" | "seller" | "buyer" | "both" | "unset";

interface ContactDraft {
  id?: string;
  name: string;
  role: string;
  info: string;
  company: string;
}

const emptyDraft: ContactDraft = { name: "", role: "", info: "", company: "" };

export function ContactsView({
  leads,
  onViewLeads,
}: {
  leads: Lead[];
  onViewLeads: (contact: Contact) => void;
}) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [draft, setDraft] = useState<ContactDraft | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { contacts: list } = await api<{ contacts: Contact[] }>(
        "/api/contacts",
      );
      setContacts(list);
    } catch {
      toast.error("联系人加载失败");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const leadCountOf = useCallback(
    (contact: Contact) =>
      leads.filter((lead) => lead.contact_id === contact.id).length,
    [leads],
  );

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return contacts.filter((contact) => {
      if (roleFilter !== "all") {
        const role = contact.role ?? "";
        if (roleFilter === "unset") {
          if (role !== "") return false;
        } else if (role !== roleFilter) {
          return false;
        }
      }
      if (!keyword) return true;
      return [contact.name, contact.info, contact.company]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(keyword));
    });
  }, [contacts, query, roleFilter]);

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error("姓名不能为空");
      return;
    }
    setBusy(true);
    try {
      if (draft.id) {
        await api(`/api/contacts/${encodeURIComponent(draft.id)}`, {
          method: "PUT",
          body: JSON.stringify(draft),
        });
        toast.success("联系人已更新");
      } else {
        await api("/api/contacts", {
          method: "POST",
          body: JSON.stringify({ contact: draft }),
        });
        toast.success("联系人已添加");
      }
      setDraft(null);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <BookUser className="size-5" />
          联系人
          <span className="text-sm font-normal text-muted-foreground">
            {visible.length} / {contacts.length}
          </span>
        </h1>
        <Button
          size="sm"
          onClick={() => setDraft({ ...emptyDraft })}
          aria-label="新增联系人"
        >
          <Plus />
          新增
        </Button>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        从微信复制名片或直接粘贴「昵称 微信号」给 Agent
        可批量导入；点联系人查看其全部货盘。
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative h-9 w-56">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            aria-label="搜索联系人"
            placeholder="姓名 / 微信号 / 公司"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-9 w-full appearance-none rounded-md border bg-card pr-3 pl-8 text-xs outline-none transition focus-visible:ring-2 focus-visible:ring-ring/30"
          />
        </div>
        <div
          className="flex h-9 items-center rounded-md bg-muted p-1"
          aria-label="按角色筛选"
        >
          {(
            [
              ["all", `全部 ${contacts.length}`],
              ["seller", "卖方"],
              ["buyer", "买方"],
              ["both", "双边"],
              ["unset", "未标"],
            ] as [RoleFilter, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={roleFilter === value}
              onClick={() => setRoleFilter(value)}
              className={`h-7 rounded px-3 text-xs font-medium whitespace-nowrap transition ${roleFilter === value ? "bg-card" : "text-muted-foreground hover:text-foreground"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {visible.length ? (
        <div className="mt-4 grid items-stretch gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((contact) => {
            const role = contact.role ?? "";
            const count = leadCountOf(contact);
            return (
              <div
                key={contact.id}
                className="flex flex-col rounded-lg border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    {contact.avatar ? (
                      <img
                        src={contact.avatar}
                        alt=""
                        className="size-9 shrink-0 rounded-full border object-cover"
                      />
                    ) : (
                      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent text-sm font-medium">
                        {(contact.name || "?").trim().charAt(0)}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {contact.name || "未命名"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {contact.info || "无联系方式"}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={roleBadgeClass[role] ?? ""}
                  >
                    {contactRoleLabels[role] ?? "未标"}
                  </Badge>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{contact.company || "—"}</span>
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 font-medium transition hover:bg-muted hover:text-foreground"
                    onClick={() => onViewLeads(contact)}
                  >
                    <Boxes className="size-3.5" />
                    货盘 {count}
                  </button>
                </div>
                <div className="mt-3 flex justify-end border-t pt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDraft({
                        id: contact.id,
                        name: contact.name,
                        role,
                        info: contact.info,
                        company: contact.company,
                      })
                    }
                    aria-label={`编辑 ${contact.name}`}
                  >
                    <Pencil />
                    编辑
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 grid min-h-56 place-items-center border-y border-dashed p-6 text-center">
          <div>
            <BookUser className="mx-auto size-6 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-medium">暂无联系人</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              点右上角新增，或把微信名片/截图丢给 Agent 导入
            </p>
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(draft)}
        onOpenChange={(open) => {
          if (!open) setDraft(null);
        }}
      >
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "编辑联系人" : "新增联系人"}</DialogTitle>
            <DialogDescription>
              角色用于区分卖方（货源）与买方（采购），可标双边。
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="grid gap-3">
              <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
                姓名 *
                <Input
                  aria-label="联系人姓名"
                  value={draft.name}
                  onChange={(event) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
                角色
                <select
                  aria-label="联系人角色"
                  value={draft.role}
                  onChange={(event) =>
                    setDraft({ ...draft, role: event.target.value })
                  }
                  className="h-9 rounded-md border border-input bg-card px-2.5 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-ring/30"
                >
                  {Object.entries(contactRoleLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
                微信号 / 联系方式
                <Input
                  aria-label="联系方式"
                  value={draft.info}
                  onChange={(event) =>
                    setDraft({ ...draft, info: event.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
                公司
                <Input
                  aria-label="公司"
                  value={draft.company}
                  onChange={(event) =>
                    setDraft({ ...draft, company: event.target.value })
                  }
                />
              </label>
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDraft(null)}
                  aria-label="取消"
                >
                  <X />
                  取消
                </Button>
                <Button onClick={() => void save()} disabled={busy}>
                  {busy ? "保存中…" : "保存"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
